"use client";

// ============================================================
// CLUBHUB – Mitglieder in eine Gruppe aufnehmen
// ============================================================
// Mehrere Personen auf einmal (KontaktAuswahl mehrfach), ein
// gemeinsames „seit“ — der Saisonbeginn, das Datum der Wahl — und
// auf Wunsch eine gemeinsame Funktion. Wer schon dabei ist, wird
// nicht doppelt aufgenommen; Ausgetretene stehen nicht zur Wahl.
// ============================================================
import { useMemo, useState } from "react";
import { Info } from "lucide-react";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogRow,
  DialogSubmit,
  DialogTitle,
  FieldLabel,
  Input,
  toast,
} from "@/components/ui";
import { KontaktAuswahl } from "@/components/mitglieder/kontakt-auswahl";
import type { ActionResult } from "@/lib/action-result";
import type { GruppeView } from "@/lib/gruppen/views";
import { alsAuswahlKontakt, stand } from "@/lib/gruppen/logik";
import { istIsoDatum } from "@/lib/gruppen/datum";
import { useGruppenBestand } from "./gruppen-bestand";
import { FUNKTION_IST_KEIN_RECHT, FunktionsFeld } from "./funktions-feld";

export function MitgliederDialog({
  offen,
  onClose,
  gruppe,
  vorgabe = [],
}: {
  offen: boolean;
  onClose: () => void;
  gruppe: GruppeView;
  /** Vorausgewaehlt — „Erneut aufnehmen“. */
  vorgabe?: string[];
}) {
  return (
    <Dialog open={offen} onClose={onClose}>
      {offen && <Formular gruppe={gruppe} vorgabe={vorgabe} />}
    </Dialog>
  );
}

function Formular({ gruppe, vorgabe }: { gruppe: GruppeView; vorgabe: string[] }) {
  const { zugehoerigkeiten, personen, heute, mitgliederHinzufuegen } = useGruppenBestand();
  const [ids, setIds] = useState<string[]>(vorgabe);
  const [seit, setSeit] = useState(heute);
  const [funktion, setFunktion] = useState("");
  const [fehler, setFehler] = useState<{ ids?: string; seit?: string }>({});

  const dabei = useMemo(
    () =>
      new Set(
        zugehoerigkeiten
          .filter((z) => z.groupId === gruppe.id && stand(z, heute) !== "beendet")
          .map((z) => z.contactId),
      ),
    [zugehoerigkeiten, gruppe.id, heute],
  );
  // Aus dem Personenbestand des Vereins (Muster oder Datenbank), in der Kurzform.
  const waehlbar = useMemo(
    () =>
      Object.values(personen)
        .filter((p) => !dabei.has(p.id) && p.status !== "FORMER" && p.status !== "DECEASED")
        .map(alsAuswahlKontakt),
    [personen, dabei],
  );
  const inGruppe = zugehoerigkeiten.filter((z) => z.groupId === gruppe.id).map((z) => z.function);
  const eine = ids.length === 1 ? personen[ids[0]!] : undefined;
  const ohneFoto = ids.filter((id) => personen[id] && !personen[id]!.isOrganization && !personen[id]!.consentPhotos);

  async function speichern(): Promise<ActionResult<{ aufgenommen: number; schonDabei: string[] }>> {
    if (ids.length === 0) return { error: "Bitte mindestens eine Person wählen.", field: "ids" };
    if (!istIsoDatum(seit)) return { error: "Bitte ein gültiges Datum für „seit“.", field: "seit" };
    return mitgliederHinzufuegen(gruppe.id, ids, seit, funktion.trim() || null);
  }

  return (
    <DialogForm
      action={speichern}
      refresh={false}
      success={(r) =>
        "aufgenommen" in r
          ? {
              title: r.aufgenommen === 1 ? `1 Person in „${gruppe.name}“ aufgenommen` : `${r.aufgenommen} Personen in „${gruppe.name}“ aufgenommen`,
              description: r.schonDabei.length > 0 ? `${r.schonDabei.length} war${r.schonDabei.length === 1 ? "" : "en"} schon dabei und wurde${r.schonDabei.length === 1 ? "" : "n"} übersprungen.` : seit > heute ? "Sie erscheinen ab dem Stichtag als aktuell." : undefined,
            }
          : null
      }
      onError={(m, r) => {
        const feld = r && "field" in r ? r.field : undefined;
        if (feld === "ids" || feld === "seit") setFehler({ [feld]: m });
        else toast.error(m);
      }}
    >
      <DialogHeader>
        <DialogTitle>Mitglieder hinzufügen</DialogTitle>
        <DialogDescription>In „{gruppe.name}“. Wer schon dabei ist, steht nicht zur Wahl.</DialogDescription>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-1.5">
          <FieldLabel>Personen</FieldLabel>
          <KontaktAuswahl
            mehrfach
            values={ids}
            onChangeMany={(v) => {
              setIds(v);
              setFehler((f) => ({ ...f, ids: undefined }));
            }}
            kontakte={waehlbar}
            placeholder="Name, Nummer, Mail, Ort …"
          />
          {fehler.ids ? (
            <p className="text-caption text-danger" role="alert">
              {fehler.ids}
            </p>
          ) : (
            <p className="text-caption text-text-tertiary">
              {ids.length > 0 ? `${ids.length} ausgewählt` : "Mehrere nacheinander antippen."}
            </p>
          )}
        </div>

        <DialogRow>
          <Input
            type="date"
            name="since"
            label="Dabei seit"
            value={seit}
            onChange={(e) => {
              setSeit(e.target.value);
              setFehler((f) => ({ ...f, seit: undefined }));
            }}
            error={fehler.seit}
            hint={seit > heute ? "Liegt in der Zukunft — zählt ab dann." : "Saisonbeginn, Wahl, Eintritt"}
          />
          <FunktionsFeld
            kind={gruppe.kind}
            gender={eine?.gender}
            wert={funktion}
            onChange={setFunktion}
            inGruppe={inGruppe}
            hint={ids.length > 1 ? "Gilt für alle Ausgewählten." : "Optional — Trainerin, Kapitän, Kassenwart …"}
          />
        </DialogRow>

        {ohneFoto.length > 0 && (
          <DialogNotice tone="warning" icon={<Info className="text-warning h-4 w-4" />}>
            {ohneFoto.length === 1 ? `${personen[ohneFoto[0]!]!.displayName} hat` : `${ohneFoto.length} der Ausgewählten haben`} keine Foto-Einwilligung — auf Mannschaftsfotos und in Spielberichten nicht zeigen.
          </DialogNotice>
        )}

        <p className="text-caption text-text-tertiary">{FUNKTION_IST_KEIN_RECHT}</p>
      </DialogContent>
      <DialogFooter>
        <DialogCancel />
        <DialogSubmit>{ids.length > 1 ? `${ids.length} aufnehmen` : "Aufnehmen"}</DialogSubmit>
      </DialogFooter>
    </DialogForm>
  );
}
